import { Dialog } from "@angular/cdk/dialog";
import { Component, computed, effect, inject, input, model, output, signal, untracked } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { CONSTANTS } from "../../../../../../constants";
import { ImageType, Media } from "../../../../../../core/database/db";
import { FormFieldComponent } from "../../../../../../shared/components/form-field/form-field.component";
import { ModalIcon, UiModalService } from "../../../../../../shared/components/modal/ui-modal.service";
import { PhotoCaptureComponent } from "../../../../../../shared/components/photo-capture/photo-capture.component";
import { SectionHeaderComponent } from "../../../../../../shared/components/section-header/section-header.component";
import { StandardBottomSheetComponent } from "../../../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { TextareaComponent } from "../../../../../../shared/components/textarea/textarea.component";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { BlobService } from "../../../../../../shared/services/blob.service";
import { BqaImageItem } from "../../../../../media/components/images-modal/bqa-image-item";
import { ImagesModalComponent } from "../../../../../media/components/images-modal/images-modal.component";
import { ImageCompressionPipelineService, MediaForCompression } from "../../../../../media/services/image-compression-pipeline.service";
import { insertAt } from "../../../../../media/utils/media-order.util";

export interface ChecklistLooksGoodSubmit {
  notes: string;
  media: Media[];
}

/**
 * Edit sheet for an already-passed BQ2/BQ3/BQ4 checklist item.
 *
 * Photos and notes are both optional here - passing an item never required documentation - and the
 * sheet doubles as the undo: "Clear Response" wipes the answer so the item goes back to unanswered.
 */
@Component({
  selector: "app-checklist-looks-good-sheet",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    StandardBottomSheetComponent,
    SectionHeaderComponent,
    FormFieldComponent,
    PhotoCaptureComponent,
    TextareaComponent,
    VerticalButtonGroupComponent
  ],
  templateUrl: "./checklist-looks-good-sheet.component.html",
  styleUrl: "./checklist-looks-good-sheet.component.scss"
})
export class ChecklistLooksGoodSheetComponent {
  private readonly dialog = inject(Dialog);
  private readonly blobService = inject(BlobService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly modalService = inject(UiModalService);
  protected readonly imageCompressionPipelineService = inject(ImageCompressionPipelineService);

  isOpen = model<boolean>(false);
  /** Checklist item being edited; rendered as the sheet's context line (may contain markup). */
  assertionText = input<string>("");
  /** Identifier new photos are parented to - the assertion's, since Pass photos hang off the item. */
  parentIdentifier = input<string>("");
  imageType = input<ImageType>(ImageType.LooksGoodConstructionManager);
  /** Note already saved against the item. */
  notes = input<string>("");
  /** Photos already saved against the item. */
  media = input<Media[]>([]);
  saving = input<boolean>(false);
  clearing = input<boolean>(false);

  readonly submitted = output<ChecklistLooksGoodSubmit>();
  readonly cleared = output<void>();
  readonly cancelled = output<void>();

  protected readonly maxNoteLength = CONSTANTS.CONSTRAINTS.MAX_NOTE_LENGTH;
  protected readonly maxPhotos = CONSTANTS.CONSTRAINTS.MAX_PHOTOS;

  protected photos = signal<Media[]>([]);
  /** Saved photos the CM removed; sent back with `deleted` set so the service soft-deletes them. */
  private removedPhotos = signal<Media[]>([]);

  protected form = new FormGroup({
    notes: new FormControl("", { nonNullable: true, validators: [Validators.maxLength(CONSTANTS.CONSTRAINTS.MAX_NOTE_LENGTH)] })
  });

  private objectUrls = new Set<string>();

  protected galleryItems = computed(() =>
    this.photos()
      .filter(image => image.base64Image || image.resourceUri)
      .map(image => new BqaImageItem({
        azureBlobUri: this.toObjectUrl(image),
        mediaIdentifier: image.fileIdentifier,
        isThumbnailOnly: image.isThumbnailOnly || false
      })));

  protected photoThumbnails = computed<SafeUrl[]>(() =>
    this.galleryItems().map(item => this.sanitizer.bypassSecurityTrustUrl(item.imageData.azureBlobUri)));

  constructor() {
    // untracked so editing the sheet's own inputs mid-session cannot wipe the CM's work.
    effect(() => {
      if (this.isOpen()) {
        untracked(() => this.resetForm());
      }
    });
  }

  protected get footerButtons(): VerticalButton[] {
    const compressing = this.imageCompressionPipelineService.isCompressingImages();
    const busy = compressing || this.saving() || this.clearing();

    let saveLabel = "Save";
    if (compressing) {
      saveLabel = "Optimizing Images...";
    } else if (this.saving()) {
      saveLabel = "Saving...";
    }

    return [
      {
        label: saveLabel,
        severity: "contrast",
        disabled: busy,
        styleClass: "bqa-primary-btn",
        action: () => this.save(),
        testId: "checklist-looks-good-sheet-btn-save"
      },
      {
        label: this.clearing() ? "Clearing..." : "Clear Response",
        severity: "danger",
        outlined: true,
        disabled: busy,
        styleClass: "bqa-danger-btn",
        action: () => this.confirmClear(),
        testId: "checklist-looks-good-sheet-btn-clear"
      },
      {
        label: "Cancel",
        severity: "secondary",
        text: true,
        disabled: busy,
        styleClass: "bqa-secondary-btn",
        action: () => this.onDismissRequested(),
        testId: "checklist-looks-good-sheet-btn-cancel"
      }
    ];
  }

  /** Drives the base sheet's click-off / swipe-down guard: only hold the sheet when there is work to lose. */
  protected get hasUnsavedChanges(): boolean {
    return this.removedPhotos().length > 0
      || this.photos().some(photo => !photo.id)
      || this.form.controls.notes.value.trim() !== this.notes().trim();
  }

  protected async onPhotosSelected(files: File[], insertIndex?: number): Promise<Media[]> {
    if (files.length === 0) {
      return [];
    }

    const mediaRecords = await this.imageCompressionPipelineService.encode(files, this.parentIdentifier(), this.imageType());
    this.photos.update(current => insertAt(current, mediaRecords, insertIndex));

    this.imageCompressionPipelineService.compress(mediaRecords, async compressed => {
      this.photos.update(current => current.map(image => {
        const match = compressed.find(m => m.fileIdentifier === image.fileIdentifier);
        return match
          ? { ...image, base64Image: match.base64Image, size: match.size, mimeType: match.mimeType }
          : image;
      }));
    });

    return mediaRecords;
  }

  protected openImagesModal(index: number): void {
    this.dialog.open<string>(ImagesModalComponent, {
      data: {
        images: this.galleryItems(),
        index,
        onDeleted: (fileIdentifier: string) => this.handleImageDeleted(fileIdentifier),
        onAnnotated: (fileIdentifier: string, file: File) => this.handleImageAnnotated(fileIdentifier, file)
      },
      backdropClass: "images-modal-backdrop"
    });
  }

  private async handleImageAnnotated(originalFileIdentifier: string, annotatedFile: File): Promise<string | undefined> {
    const index = this.photos().findIndex(image => image.fileIdentifier === originalFileIdentifier);
    this.handleImageDeleted(originalFileIdentifier);
    const [added] = await this.onPhotosSelected([annotatedFile], index);
    return added?.fileIdentifier;
  }

  protected onSheetOpenChange(isOpen: boolean): void {
    if (!isOpen && this.isOpen()) {
      this.cancel();
    }
  }

  /**
   * Reached from Cancel, and from a backdrop tap or swipe-down while the form has edits (those
   * gestures close the sheet on their own once nothing has changed).
   */
  protected onDismissRequested(): void {
    if (!this.hasUnsavedChanges) {
      this.cancel();
      return;
    }

    this.modalService.showModal({
      icon: ModalIcon.None,
      header: "Cancel",
      subHeader: "Are you sure you want to proceed?",
      message: "Unsaved changes will be lost.",
      cancelLabel: "No",
      onCancel: () => { },
      actionLabel: "Yes",
      onAction: () => this.cancel(),
      isDestructive: true
    });
  }

  private confirmClear(): void {
    this.modalService.showModal({
      icon: ModalIcon.None,
      header: "Clear Response",
      subHeader: "Are you sure you want to proceed?",
      message: "This item will go back to unanswered and any photos and notes will be removed.",
      cancelLabel: "No",
      onCancel: () => { },
      actionLabel: "Yes",
      onAction: () => this.cleared.emit(),
      isDestructive: true
    });
  }

  private handleImageDeleted(fileIdentifier: string): void {
    const removed = this.photos().find(image => image.fileIdentifier === fileIdentifier);
    if (removed?.id) {
      this.removedPhotos.update(current => [...current, { ...removed, deleted: 1 }]);
    }

    this.photos.update(current => current.filter(image => image.fileIdentifier !== fileIdentifier));
  }

  private async save(): Promise<void> {
    if (this.form.invalid) {
      return;
    }

    await this.imageCompressionPipelineService.waitForCompletion();

    const media = [...this.photos(), ...this.removedPhotos()].map(image => {
      const { file: _file, ...rest } = image as MediaForCompression;
      return rest;
    });

    this.submitted.emit({ notes: this.form.controls.notes.value.trim(), media });
  }

  private cancel(): void {
    this.isOpen.set(false);
    this.cancelled.emit();
  }

  private resetForm(): void {
    this.releaseObjectUrls();
    this.photos.set([...this.media()]);
    this.removedPhotos.set([]);
    this.form.reset({ notes: this.notes() });
  }

  private toObjectUrl(image: Media): string {
    if (!image.base64Image) {
      return image.resourceUri ?? "";
    }

    const url = URL.createObjectURL(this.blobService.base64ToBlob(image.base64Image));
    this.objectUrls.add(url);
    return url;
  }

  private releaseObjectUrls(): void {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.objectUrls.clear();
  }
}
